import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { getAnyProduct, getProduct } from "../config";

const fmt = (n) => `${n.toFixed(2).replace(".", ",")} €`;

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal, shipping, shippingFree, hasKit, checkout } = useCart();

  const progress = Math.min(100, (subtotal / 40) * 100);
  const upsell = hasKit ? getProduct("pastilles") : getProduct("kit");
  const showUpsell = upsell && !items.some((i) => i.id === upsell.id);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 90 }} />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "100%", maxWidth: 420, background: "var(--surface)", borderLeft: "1px solid var(--border)", zIndex: 95, display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px", borderBottom: "1px solid var(--border)" }}>
              <span className="display" style={{ textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>Panier</span>
              <button onClick={() => setOpen(false)} data-testid="cart-close" aria-label="Fermer" style={{ background: "none", border: "none", color: "var(--text)", cursor: "pointer" }}><X /></button>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
              {items.length === 0 && <p style={{ color: "var(--text-muted)" }} data-testid="cart-empty">Votre panier est vide.</p>}
              {items.map((it) => {
                const p = getAnyProduct(it.id);
                if (!p) return null;
                return (
                  <div key={`${it.id}-${it.variant}`} data-testid={`cart-line-${it.id}`} style={{ display: "flex", gap: 14, marginBottom: 20 }}>
                    <img src={p.image} alt={p.name} style={{ width: 64, height: 80, objectFit: "cover", borderRadius: 4, border: "1px solid var(--border)" }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{p.short_name}</div>
                      {it.variant && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{it.variant}</div>}
                      <div className="price" style={{ fontSize: 15, marginTop: 4 }}>{fmt(p.price_eur * it.quantity)}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
                        <button onClick={() => updateQty(it.id, it.variant, -1)} data-testid={`qty-minus-${it.id}`} style={qtyBtn}><Minus size={14} /></button>
                        <span data-testid={`qty-${it.id}`}>{it.quantity}</span>
                        <button onClick={() => updateQty(it.id, it.variant, 1)} data-testid={`qty-plus-${it.id}`} style={qtyBtn}><Plus size={14} /></button>
                        <button onClick={() => removeItem(it.id, it.variant)} data-testid={`remove-${it.id}`} style={{ ...qtyBtn, marginLeft: "auto", borderColor: "transparent" }}><Trash2 size={15} /></button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {showUpsell && items.length > 0 && (
                <Link to={upsell.route} onClick={() => setOpen(false)} data-testid="cart-upsell" className="card" style={{ display: "flex", gap: 12, padding: 12, marginTop: 12, alignItems: "center" }}>
                  <img src={upsell.image} alt={upsell.name} style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 4 }} />
                  <div style={{ fontSize: 13 }}>
                    <div style={{ color: "var(--accent-light)" }}>Compléter avec {upsell.short_name}</div>
                    <div className="price" style={{ fontSize: 14 }}>{fmt(upsell.price_eur)}</div>
                  </div>
                </Link>
              )}
            </div>

            {items.length > 0 && (
              <div style={{ padding: 20, borderTop: "1px solid var(--border)" }}>
                {hasKit ? (
                  <div data-testid="shipping-line" style={{ color: "var(--accent)", fontSize: 14, marginBottom: 10, fontWeight: 600 }}>Livraison offerte — incluse dans l'offre découverte ✓</div>
                ) : shippingFree ? (
                  <div data-testid="shipping-line" style={{ color: "var(--accent-light)", fontSize: 14, marginBottom: 10 }}>Livraison offerte ✓</div>
                ) : (
                  <div data-testid="shipping-line" style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 6 }}>Livraison 4,90 € — offerte dès 40 €</div>
                    <div style={{ height: 4, background: "var(--border)", borderRadius: 4 }} data-testid="shipping-progress">
                      <div style={{ height: 4, width: `${progress}%`, background: "var(--accent)", borderRadius: 4, transition: "width .3s" }} />
                    </div>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginBottom: 4 }}>
                  <span style={{ color: "var(--text-muted)" }}>Sous-total</span>
                  <span data-testid="cart-subtotal">{fmt(subtotal)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginBottom: 16 }}>
                  <span style={{ color: "var(--text-muted)" }}>Livraison</span>
                  <span>{shipping === 0 ? "Offerte" : fmt(shipping)}</span>
                </div>
                <button className="btn btn-primary" style={{ width: "100%" }} data-testid="checkout-button" onClick={checkout}>Passer commande</button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

const qtyBtn = { width: 28, height: 28, borderRadius: 4, border: "1px solid var(--border)", background: "transparent", color: "var(--text)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" };
